import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  Add as AddIcon,
  Folder as FolderIcon,
  Science as ScienceIcon,
  Timeline as TimelineIcon,
  ContentCopy as CopyIcon,
  NavigateNext as NavigateNextIcon,
  Upload as UploadIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import { labelsForKeys } from '../utils/formValidation';
import FormValidationAlert from '../components/FormValidationAlert';
import { hasCapability, CAP } from '../utils/permissions';
import { useCopy } from '../contexts/CopyContext';
import sandboxesService from '../services/sandboxesService';
import projectsService from '../services/projectsService';
import usersService from '../services/usersService';
import DragOverlay from '../components/DragOverlay';
import { Sandbox, CreateSandboxForm, Project, Revision, User } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';

const SandboxesPage: React.FC = () => {
  const { user } = useAuth();
  const { copiedSandbox, pasteSandbox, copySandbox, setIsDragging, isDragging } = useCopy();
  const navigate = useNavigate();
  const { projectId, revisionId } = useParams<{ projectId: string; revisionId: string }>();

  // State management
  const [sandboxes, setSandboxes] = useState<Sandbox[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [revision, setRevision] = useState<Revision | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fieldErrors = useFormFieldErrors();

  // Dialog states
  const [createSandboxOpen, setCreateSandboxOpen] = useState(false);
  const [showDragOverlay, setShowDragOverlay] = useState(false);
  const [draggedSandbox, setDraggedSandbox] = useState<Sandbox | null>(null);

  // Custom drag detection
  const [dragState, setDragState] = useState({
    isDragging: false,
    startX: 0,
    startY: 0,
    draggedElement: null as HTMLElement | null,
  });

  // Form states
  const [sandboxForm, setSandboxForm] = useState<CreateSandboxForm>({
    name: '',
    file_path: '',
    software: 'OrcaFlex',
  });


  // Load initial data
  useEffect(() => {
    if (projectId && revisionId) {
      loadData();
    }
  }, [projectId, revisionId]);

  // Handle global mouse events for custom drag detection
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      handleMouseMove(e as any);
    };

    const handleGlobalMouseUp = () => {
      handleMouseUp();
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [dragState, sandboxes]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load sandboxes, project, revision, and users first
      const [sandboxesData, projectData, revisionData, usersData] = await Promise.all([
        sandboxesService.getSandboxes(parseInt(projectId!), parseInt(revisionId!)).catch(err => {
          console.error('Error loading sandboxes:', err);
          return [];
        }),
        loadProject(),
        loadRevision(),
        loadUsers(),
      ]);

      setSandboxes(sandboxesData);
      setProject(projectData);
      setRevision(revisionData);
      setUsers(usersData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadProject = async (): Promise<Project | null> => {
    try {
      const projects = await projectsService.getProjects();
      return projects.find(p => p.id === parseInt(projectId!)) || null;
    } catch (err) {
      console.error('Error loading project:', err);
      return null;
    }
  };

  const loadRevision = async (): Promise<Revision | null> => {
    try {
      const revisions = await projectsService.getRevisions(parseInt(projectId!));
      return revisions.find(r => r.id === parseInt(revisionId!)) || null;
    } catch (err) {
      console.error('Error loading revision:', err);
      return null;
    }
  };

  const loadUsers = async (): Promise<User[]> => {
    try {
      // Load project-specific users (PM + engineers) instead of all users
      const users = await projectsService.getProjectUsers(parseInt(projectId!));
      return users.map(user => ({
        id: user.id,
        name: user.name,
        email: '', // Not needed for reviewer selection
        roles: [], // Not needed for reviewer selection
      }));
    } catch (err) {
      console.error('Error loading project users:', err);
      return [];
    }
  };

  const handleCreateSandbox = async () => {
    const missing: string[] = [];
    if (!sandboxForm.name.trim()) missing.push('name');
    if (!sandboxForm.file_path.trim()) missing.push('file_path');
    if (missing.length > 0) {
      fieldErrors.showMissing(missing, labelsForKeys(missing, {
        name: 'Sandbox name',
        file_path: 'File path',
      }));
      return;
    }
    try {
      await sandboxesService.createSandbox(
        parseInt(projectId!),
        parseInt(revisionId!),
        sandboxForm
      );
      setCreateSandboxOpen(false);
      setSandboxForm({
        name: '',
        file_path: '',
        software: 'OrcaFlex',
      });
      loadData(); // Refresh the list
    } catch (err: any) {
      fieldErrors.show(err.response?.data?.error || 'Failed to create sandbox');
    }
  };


  const handleSandboxClick = (sandbox: Sandbox) => {
    navigate(`/lcms/${projectId}/${revisionId}/${sandbox.id}`);
  };

  const handleCopySandbox = (sandbox: Sandbox) => {
    copySandbox({
      id: sandbox.id,
      name: sandbox.name,
      file_path: sandbox.file_path,
      software: sandbox.software,
      project_id: parseInt(projectId!),
      revision_id: parseInt(revisionId!),
      history_id: 0, // Will be set when getting revision data
    });
    setError(null);
  };

  const handlePasteSandbox = async () => {
    if (!copiedSandbox) return;

    try {
      const success = await pasteSandbox(
        parseInt(projectId!),
        parseInt(revisionId!)
      );

      if (success) {
        setError(null);
        loadData(); // Refresh the list
      } else {
        setError('Failed to paste sandbox');
      }
    } catch (err) {
      setError('Failed to paste sandbox');
      console.error('Paste error:', err);
    }
  };

  const handleMouseDown = (e: React.MouseEvent, sandbox: Sandbox) => {
    if (!hasCapability(user, CAP.MANAGE_SANDBOXES)) return;

    setDragState({
      isDragging: false,
      startX: e.clientX,
      startY: e.clientY,
      draggedElement: e.currentTarget as HTMLElement,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragState.draggedElement || dragState.isDragging) return;

    const deltaX = Math.abs(e.clientX - dragState.startX);
    const deltaY = Math.abs(e.clientY - dragState.startY);
    const dragThreshold = 10; // pixels

    if (deltaX > dragThreshold || deltaY > dragThreshold) {
      // Find the sandbox from the dragged element
      const sandboxId = dragState.draggedElement.getAttribute('data-sandbox-id');
      const sandbox = sandboxes.find(s => s.id === parseInt(sandboxId || '0'));

      if (sandbox) {
        setDraggedSandbox(sandbox);
        setIsDragging(true);
        setShowDragOverlay(true);

        copySandbox({
          id: sandbox.id,
          name: sandbox.name,
          file_path: sandbox.file_path,
          software: sandbox.software,
          project_id: parseInt(projectId!),
          revision_id: parseInt(revisionId!),
          history_id: 0,
        });
      }
    }
  };

  const handleMouseUp = () => {
    if (dragState.isDragging) {
      // Reset drag state after a delay
      setTimeout(() => {
        setShowDragOverlay(false);
        setDraggedSandbox(null);
        setIsDragging(false);
        setDragState({
          isDragging: false,
          startX: 0,
          startY: 0,
          draggedElement: null,
        });
      }, 200);
    } else {
      // Reset drag state immediately if not dragging
      setDragState({
        isDragging: false,
        startX: 0,
        startY: 0,
        draggedElement: null,
      });
    }
  };

  const handleDrop = async (destinationProjectId: number, destinationRevisionId: number) => {
    setShowDragOverlay(false);
    setDraggedSandbox(null);
    setIsDragging(false);

    if (!copiedSandbox) return;

    try {
      const success = await pasteSandbox(destinationProjectId, destinationRevisionId);
      if (success) {
        setError(null);
        // If dropping in the same revision, refresh the list
        if (destinationProjectId === parseInt(projectId!) && destinationRevisionId === parseInt(revisionId!)) {
          loadData();
        }
      } else {
        setError('Failed to copy sandbox');
      }
    } catch (err) {
      setError('Failed to copy sandbox');
      console.error('Drop error:', err);
    }
  };

  const handleDragOverlayCancel = () => {
    setShowDragOverlay(false);
    setDraggedSandbox(null);
    setIsDragging(false);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'c' && draggedSandbox) {
          e.preventDefault();
          handleCopySandbox(draggedSandbox);
        } else if (e.key === 'v' && copiedSandbox) {
          e.preventDefault();
          handlePasteSandbox();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [draggedSandbox, copiedSandbox]);

  const canCreateSandbox = hasCapability(user, CAP.MANAGE_SANDBOXES);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }


  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
        aria-label="breadcrumb"
      >
        <Link to="/projects" style={{ textDecoration: 'none', color: 'grey' }}>
          <Typography variant="body1" sx={{ color: 'inherit', '&:hover': { textDecoration: 'underline' } }}>
            Projects
          </Typography>
        </Link>
        <Link to={`/projects/${projectId}/revisions`} style={{ textDecoration: 'none', color: 'grey' }}>
          <Typography variant="body1" sx={{ color: 'inherit', '&:hover': { textDecoration: 'underline' } }}>
            {project?.name || `Project ${projectId}`}
          </Typography>
        </Link>
        <Link to={`/projects/${projectId}/revisions`} style={{ textDecoration: 'none', color: 'grey' }}>
          <Typography variant="body1" sx={{ color: 'inherit', '&:hover': { textDecoration: 'underline' } }}>
            {revision?.revision_name || `Revision ${revisionId}`}
          </Typography>
        </Link>
        <Typography color="text.primary">Sandboxes</Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 500, mb: 1 }}
          >
            Sandboxes
          </Typography>
          {project && revision && (
            <Typography variant="subtitle1" color="text.secondary">
              {project.name} - {revision.revision_name} (Step {revision.revision_step})
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {canCreateSandbox && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateSandboxOpen(true)}
              aria-label="Create new sandbox"
            >
              New Sandbox
            </Button>
          )}


          {copiedSandbox && canCreateSandbox && (
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={handlePasteSandbox}
              color="secondary"
              aria-label={`Paste copied sandbox: ${copiedSandbox.name}`}
              sx={{
                borderStyle: 'dashed',
                '&:hover': {
                  borderStyle: 'dashed',
                  backgroundColor: 'secondary.light',
                },
              }}
            >
              Paste "{copiedSandbox.name}"
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {error}
        </Alert>
      )}

      {/* Sandboxes Section */}
      {sandboxes.length > 0 && (
        <>
          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
            Sandboxes
          </Typography>
          <Grid container spacing={3}>
            {sandboxes.map((sandbox) => (
              <Grid item xs={12} md={6} lg={4} key={sandbox.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: canCreateSandbox ? 'grab' : 'pointer',
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                    opacity: draggedSandbox?.id === sandbox.id ? 0.5 : 1,
                    userSelect: 'none', // Prevent text selection during drag
                  }}
                  onClick={() => handleSandboxClick(sandbox)}
                  onMouseDown={(e) => handleMouseDown(e, sandbox)}
                  data-sandbox-id={sandbox.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`View LCMs for sandbox ${sandbox.name}. Press Ctrl+C to copy or drag to copy`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSandboxClick(sandbox);
                    } else if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                      e.preventDefault();
                      handleCopySandbox(sandbox);
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <ScienceIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 500 }}>
                        {sandbox.name}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={<TimelineIcon />}
                        label={sandbox.software}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      ID: {sandbox.id}
                    </Typography>

                    {sandbox.file_path && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Model: {sandbox.file_path.split(/[/\\]/).pop()}
                      </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary">
                      Created: {new Date().toLocaleDateString()} {/* TODO: Add created date from API */}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                    <Button size="small" color="primary">
                      View LCMs
                    </Button>

                    {canCreateSandbox && (
                      <Tooltip title="Copy Sandbox (Ctrl+C)">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopySandbox(sandbox);
                          }}
                          aria-label={`Copy sandbox ${sandbox.name}`}
                          sx={{
                            color: copiedSandbox?.id === sandbox.id ? 'primary.main' : 'inherit',
                          }}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}


      {sandboxes.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <ScienceIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No sandboxes found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first sandbox to get started with load case modeling.
          </Typography>
          {canCreateSandbox && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateSandboxOpen(true)}
            >
              Create Your First Sandbox
            </Button>
          )}
        </Box>
      )}

      {/* Create Sandbox Dialog */}
      <Dialog
        open={createSandboxOpen}
        onClose={() => { setCreateSandboxOpen(false); fieldErrors.clear(); }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="create-sandbox-title"
      >
        <DialogTitle id="create-sandbox-title">Create New Sandbox</DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); handleCreateSandbox(); }}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Sandbox Name"
              fullWidth
              required
              value={sandboxForm.name}
              error={fieldErrors.invalid('name')}
              onChange={(e) => {
                fieldErrors.clearField('name');
                setSandboxForm({ ...sandboxForm, name: e.target.value });
              }}
              aria-describedby="sandbox-name-helper"
            />
            <Typography id="sandbox-name-helper" variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
              Enter a descriptive name for the sandbox
            </Typography>

            <TextField
              margin="dense"
              label="File Path"
              fullWidth
              required
              value={sandboxForm.file_path}
              error={fieldErrors.invalid('file_path')}
              onChange={(e) => {
                fieldErrors.clearField('file_path');
                setSandboxForm({ ...sandboxForm, file_path: e.target.value });
              }}
              aria-describedby="file-path-helper"
            />
            <Typography id="file-path-helper" variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
              Path to the model file
            </Typography>

            <TextField
              margin="dense"
              label="Software"
              fullWidth
              value={sandboxForm.software}
              onChange={(e) => setSandboxForm({ ...sandboxForm, software: e.target.value })}
              aria-describedby="software-helper"
            />
            <Typography id="software-helper" variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
              Analysis software (default: OrcaFlex)
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setCreateSandboxOpen(false); fieldErrors.clear(); }}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
            >
              Create Sandbox
            </Button>
          </DialogActions>
        </Box>
      </Dialog>


      {/* Drag Overlay */}
      <DragOverlay
        isVisible={showDragOverlay}
        onDrop={handleDrop}
        onCancel={handleDragOverlayCancel}
      />
    </Box>
  );
};

export default SandboxesPage;